import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Button, Col, Container, Row, Tab, Tabs, OverlayTrigger, Tooltip, Card, Form } from "react-bootstrap";
import { useParamIds } from "./Data/Common";
import { ColDef, GridReadyEvent, ICellRendererParams, RowClickedEvent } from 'ag-grid-community';
import { useLoaderData, useLocation, useNavigate, useRevalidator } from "react-router-dom";
import { Session } from "./Data/ApiTypes";
import { DataGrid } from "./Controls/DataGrid";
import { SessionType } from "./Data/ApiTransport";
import { PageTitle } from "./Controls/PageTitle";
import { SimpleButton } from "./Controls/Button";
import { DateTimeFormatter, Duration, Instant, LocalDate, LocalDateTime, ZoneId } from "@js-joda/core";
import { Locale } from "@js-joda/locale_en-us";
import { Badge } from "react-bootstrap";
import { DateTimeFmt } from "./Data/Date";
import { ParkingValidationRequestApi, ReservationApi, ServerRequestsParkingValidationData } from "./shared/api/axios-client";
import { CheckCircle, Trash, ArrowClockwise, Camera, QrCode } from "react-bootstrap-icons";
import { createApiService } from "./shared/apiConfig";
import { toast } from "react-toastify";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { PlateScannerModal, PlateSessionInfo } from "./components/plate-scanner/PlateScannerModal";
import { QrScannerModal, QrSessionInfo } from "./components/qr-scanner/QrScannerModal";
import { userCanNavigateToParkerFromSessions } from "./helpers/RoleManager";
import { useAuthorization } from "./hooks/useAuthorization";
import { facilityCache } from "./services/FacilityCache";
import { FEATURES, isFeatureEnabled } from "./Data/Features";
import { Api } from "./Data/Api";

interface PlateButtonProps {
    className?: string;
    onClick:    () => void;
    children:   React.ReactNode;
}

export function PlateButton( { className = "", onClick, children }: PlateButtonProps ) {    
    return <div style={{ flex: "0 0 auto" }}>
        <SimpleButton variant="sm"
                       className={`shadow d-flex-align-center p-1 px-2 justify-content-center`}
                       onClick={onClick}>
            {children}
        </SimpleButton>
    </div>;
}

function PlateRenderer( params: ICellRendererParams<any, string> ) {
    if( !params.value ) { return <></>; }
    var plates = params.value.split( ", " );
    return <div className={`d-flex flex-row py-1 align-content-center gap-2`} style={{ maxWidth: '100%', overflow: 'hidden' }}>
        {plates.map( p => <PlateButton onClick={ () => 0 } key={p}>
            {p}
        </PlateButton> )}
    </div>;
}

function showSessionOption( s: Session ): string {
    return s.reservation!.rateProgram.name;
}

const mobileTimeFmt = DateTimeFormatter.ofPattern( "M/dd h:mm a" ).withLocale( Locale.US );

const mobileTimeFmtTimeOnly = DateTimeFormatter.ofPattern( "h:mm a" ).withLocale( Locale.US );

function formatMobileDateTime( dt: LocalDateTime ): string {
    if( dt.toLocalDate().equals( LocalDate.now() ) )
        return mobileTimeFmtTimeOnly.format( dt );
    return mobileTimeFmt.format( dt );
}

const SPOTSYNC_SOURCE = "SpotSync";

function isExternalSessionSource( source: string | null | undefined ): source is string {
    return !!source && source !== SPOTSYNC_SOURCE;
}

function SessionSourceBadge( { source, className = "" }: { source: string; className?: string } ) {
    return (
        <Badge bg="secondary" className={`fw-normal ${className}`.trim()}>
            {source}
        </Badge>
    );
}

function formatTimeRemaining( end: LocalDateTime ): string | null {
    const now = LocalDateTime.now();
    if( !end.isAfter( now ) ) return null;
    const mins = Duration.between( now, end ).toMinutes();
    if( mins >= 60 ) return null;
    if( mins < 1 ) return "<1m left";
    return `${mins}m left`;
}

function SessionMobileCard( { session, onClick }: { session: Session; onClick: () => void } ) {
    const timeLeft = formatTimeRemaining( getSessionEndTime( session ) );

    return (
        <Card
            className="mb-2 rounded-3 border mx-0"
            style={{ cursor: "pointer", minWidth: 0 }}
            onClick={onClick}
        >
            <Card.Body className="py-2 px-3">
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <div
                        className="d-flex align-items-center justify-content-center rounded-3 fw-semibold text-center flex-shrink-0"
                        style={{
                            width: "76px",
                            minWidth: "76px",
                            minHeight: "46px",
                            border: "1.5px solid #D61F84",
                            fontSize: "0.75rem",
                            fontFamily: "monospace",
                            letterSpacing: "0.04em",
                            padding: "4px 6px",
                            wordBreak: "break-all",
                        }}
                    >
                        {session.vehicles || "—"}
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="fw-semibold small text-truncate">
                                {formatMobileDateTime( getSessionStartTime( session ) )}
                                {" \u2192 "}
                                {formatMobileDateTime( getSessionEndTime( session ) )}
                            </div>
                            {timeLeft && (
                                <span
                                    className="rounded-pill flex-shrink-0 ms-2"
                                    style={{
                                        background: "rgba(220,53,69,0.12)",
                                        color: "#dc3545",
                                        fontWeight: 600,
                                        fontSize: "0.65rem",
                                        padding: "2px 7px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {timeLeft}
                                </span>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-2 text-muted text-truncate small">
                            <span className="text-truncate">{session.parker?.name ?? "—"}</span>
                            {isExternalSessionSource( session.source ) && (
                                <SessionSourceBadge source={session.source} className="flex-shrink-0" />
                            )}
                        </div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
}

function formatDateTimeWithToday(dateTime: LocalDateTime): string {
    const today = LocalDate.now();
    const dateToCheck = dateTime.toLocalDate();
    
    if (dateToCheck.equals(today)) {
        const timeFormatter = DateTimeFormatter.ofPattern("h:mm a").withLocale(Locale.US);
        return `Today ${timeFormatter.format(dateTime)}`;
    }
    return DateTimeFmt.min.format(dateTime);
}

enum SessionTab {
    Live = "live",
    Upcoming = "upcoming",
    Requests = "requests",
    Previous = "previous"
}

function getSessionStartTime(session: Session): LocalDateTime {
    return session.reservation!.start;
}

function getSessionEndTime(session: Session): LocalDateTime {
    return session.reservation!.end ?? LocalDateTime.now();
}

function isLiveSession(session: Session): boolean {
    const now = LocalDateTime.now();
    const start = getSessionStartTime(session);
    const end = getSessionEndTime(session);
    return !start.isAfter(now) && !end.isBefore(now);
}

function isUpcomingSession(session: Session): boolean {
    const now = LocalDateTime.now();
    const start = getSessionStartTime(session);
    return start.isAfter(now);
}

function isPreviousSession(session: Session): boolean {
    const now = LocalDateTime.now();
    const end = getSessionEndTime(session);
    return end.isBefore(now);
}

const SESSION_POLL_MS = 30_000;
const SESSION_AUTO_REFRESH_CAP_MS = 2 * 60 * 60 * 1000;

function maxReservationCreatedWhen( sessions: Session[] ): Instant | undefined {
    let m: Instant | undefined;
    for( const s of sessions ) {
        if( !s.reservation ) continue;
        const c = s.reservation.createdWhen;
        if( m == null || c.isAfter( m ) ) m = c;
    }
    return m;
}

function formatLastRefreshClock( d: Date ): string {
    return d.toLocaleTimeString( undefined, { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true } );
}

function mergeSessionsByReservationId( existing: Session[], delta: Session[] ): Session[] {
    const map = new Map<number, Session>();
    const order: number[] = [];
    for( const s of existing ) {
        const id = s.reservation?.reservationId;
        if( id == null ) continue;
        map.set( id, s );
        order.push( id );
    }
    for( const s of delta ) {
        const id = s.reservation?.reservationId;
        if( id == null ) continue;
        if( !map.has( id ) ) order.push( id );
        map.set( id, s );
    }
    return order.map( id => map.get( id )! );
}

const QR_ENTRY_TODAY_PINK = "#ec4899";

const qrEntryStatShell: CSSProperties = {
    borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
    borderRight: "1px solid rgba(0, 0, 0, 0.08)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    borderTop: "none",
    boxShadow: "0 0px 1px rgba(0, 0, 0, 0.05), 0 2px 6px rgba(0, 0, 0, 0.04)",
};

function QrEntriesTodayStat( { count }: { count: number | null } ) {
    const label = count === null ? "QR entry confirmations today, loading" : `QR entry confirmations today: ${count}`;
    const display = count === null ? "—" : count.toLocaleString();
    return (
        <div
            className="rounded-3 bg-white overflow-hidden flex-shrink-0 align-self-start"
            style={{ ...qrEntryStatShell, maxWidth: "12rem" }}
            role="status"
            aria-label={ label }
        >
            <div
                className="w-100"
                style={{ height: "0.16rem", backgroundColor: QR_ENTRY_TODAY_PINK }}
                aria-hidden
            />
            <div className="px-2 py-1 px-sm-3">
                <div
                    className="text-uppercase mb-0 lh-1 text-nowrap"
                    style={{
                        letterSpacing: "0.05em",
                        fontSize: "0.6rem",
                        color: "#6b7280",
                        fontWeight: 600,
                    }}
                >
                    QR entries today
                </div>
                <div className="lh-1 text-dark" style={{ marginTop: "0.08rem" }}>
                    <span className="fw-bold" style={{ fontSize: "1.05rem" }}>
                        {display}
                    </span>
                </div>
            </div>
        </div>
    );
}

export function FacilitySessionListPage() {
    const nav  = useNavigate();
    const loc  = useLocation();
    const last = loc.pathname.split("/").pop();
    const tab  = Object.values(SessionTab).includes(last as SessionTab) ? last as SessionTab : SessionTab.Live;
    const revalidator = useRevalidator();
   
    const { facilityId } = useParamIds();
    const [sessionsFromLoader, validationRequests] = useLoaderData() as [Session[], ServerRequestsParkingValidationData[]];
    const [sessions, setSessions] = useState<Session[]>( () => sessionsFromLoader );
    const [validationRequestsState, setValidationRequestsState] = useState( validationRequests );
    const sessionsRef = useRef( sessions );
    sessionsRef.current = sessions;

    const [autoRefresh, setAutoRefresh] = useState( true );
    const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>( () => new Date() );
    const [isSessionPollLoading, setIsSessionPollLoading] = useState( false );
    const autoRefreshStartedAtRef = useRef( Date.now() );
    const [nowTick, setNowTick] = useState( 0 );
    const [entryConfirmedTodayCount, setEntryConfirmedTodayCount] = useState<number | null>( null );

    const validationApi  = useMemo( () => createApiService( ParkingValidationRequestApi ), [] );
    const reservationApi = useMemo( () => createApiService( ReservationApi ), [] );

    const refreshEntryConfirmedTodayCount = useCallback( async () => {
        const facility = facilityCache.getFacility( facilityId );
        const tz = facility?.timeZone;
        if ( !isFeatureEnabled( facility ?? { features: [] } as any, FEATURES.TRANSIENT_QR_SCAN ) || !tz ) return;
        try {
            const { data } = await reservationApi.entryConfirmedTodayCount( { facilityId, timeZone: tz } );
            setEntryConfirmedTodayCount( data?.count ?? 0 );
        } catch { /* stat only */ }
    }, [facilityId, reservationApi] );

    useEffect( () => {
        setSessions( sessionsFromLoader );
        setValidationRequestsState( validationRequests );
        setLastRefreshedAt( new Date() );
    }, [sessionsFromLoader, validationRequests] );

    useEffect( () => {
        if( autoRefresh ) autoRefreshStartedAtRef.current = Date.now();
    }, [autoRefresh] );

    const runSessionPoll = useCallback( async () => {
        if( Date.now() - autoRefreshStartedAtRef.current >= SESSION_AUTO_REFRESH_CAP_MS ) {
            setAutoRefresh( false );
            toast.info( "Auto-refresh stopped after 2 hours." );
            return;
        }
        const since = maxReservationCreatedWhen( sessionsRef.current );
        const facility = facilityCache.getFacility( facilityId );
        const tz = facility?.timeZone;
        const doQrCount = isFeatureEnabled( facility ?? { features: [] } as any, FEATURES.TRANSIENT_QR_SCAN ) && !!tz;
        setIsSessionPollLoading( true );
        try {
            const listPromise = Api.facilitySessionList( facilityId, {
                ...( tz ? { timeZone: tz } : {} ),
                ...( since ? { createdSince: since } : {} ),
                silent: true,
            } );
            const countPromise = doQrCount
                ? reservationApi.entryConfirmedTodayCount( { facilityId, timeZone: tz! } )
                    .then( r => r.data?.count ?? 0 )
                    .catch( () => undefined as undefined | number )
                : Promise.resolve( undefined as undefined | number );
            const [res, count] = await Promise.all( [listPromise, countPromise] );
            if( res.isOk ) {
                const data = res.value;
                setSessions( prev => since ? mergeSessionsByReservationId( prev, data ) : data );
                setLastRefreshedAt( new Date() );
            } else {
                toast.error( res.error ?? "Session refresh failed" );
            }
            if( count !== undefined ) {
                setEntryConfirmedTodayCount( count );
            }
        } finally {
            setIsSessionPollLoading( false );
        }
    }, [facilityId, reservationApi] );

    useEffect( () => {
        if( !autoRefresh ) return;
        const id = window.setInterval( () => {
            void runSessionPoll();
            setNowTick( t => t + 1 );
        }, SESSION_POLL_MS );
        return () => window.clearInterval( id );
    }, [autoRefresh, runSessionPoll] );

    const alprEnabled = isFeatureEnabled(facilityCache.getFacility(facilityId) ?? { features: [] } as any, FEATURES.TRANSIENT_PHONE_ALPR);
    const qrEnabled   = isFeatureEnabled(facilityCache.getFacility(facilityId) ?? { features: [] } as any, FEATURES.TRANSIENT_QR_SCAN);

    useEffect( () => {
        if( !qrEnabled ) {
            setEntryConfirmedTodayCount( null );
            return;
        }
        const tz = facilityCache.getFacility( facilityId )?.timeZone;
        if( !tz ) {
            setEntryConfirmedTodayCount( null );
            return;
        }
        setEntryConfirmedTodayCount( null );
        void refreshEntryConfirmedTodayCount();
    }, [qrEnabled, facilityId, refreshEntryConfirmedTodayCount] );
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingAction, setPendingAction] = useState<{
    action: "approve" | "reject";
    requestId: number;
    } | null>(null);
    const [search, setSearch] = useState("");
    const [liveVisibleCount, setLiveVisibleCount] = useState(10);
    const [upcomingVisibleCount, setUpcomingVisibleCount] = useState(10);
    const [requestsVisibleCount, setRequestsVisibleCount] = useState(10);
    const [previousVisibleCount, setPreviousVisibleCount] = useState(10);
    const [showPlateScanModal, setShowPlateScanModal] = useState(false);
    const [showQrScanModal,   setShowQrScanModal]   = useState(false);

    // nowTick ensures these re-evaluate on every poll tick even when no new sessions arrive
    const liveSessions     = useMemo( () =>
        sessions.filter( isLiveSession ).sort( ( a, b ) => getSessionEndTime( a ).compareTo( getSessionEndTime( b ) ) ),
        [sessions, nowTick] );
    const upcomingSessions = useMemo( () => sessions.filter( isUpcomingSession ),  [sessions, nowTick] );
    const previousSessions = useMemo( () => sessions.filter( isPreviousSession ),  [sessions, nowTick] );

    const ValidationBadgeRenderer = ({ value, data }: { value: string; data: Session }) => {
    return (
        <div className="flex items-center gap-1">
        {data.reservation?.validationRequestId && (
            <Badge className="me-2">
            Validation
            </Badge>
        )}
        <span>{value}</span>
        </div>
    );
    };

    const activeColumns = useMemo<ColDef<Session>[]>(() => [
        { 
            field: "parker.parkerId",     
            headerName: "ID", 
            hide: true,
            flex: 1,
            sortable: true
        },
        { 
            field: "parker.name",         
            headerName: "Name",
            flex: 2,
            sortable: true,
            cellRenderer: ValidationBadgeRenderer 
        },     
        { 
            field: "reservation.confirmationNum",         
            headerName: "Confirmation #",
            flex: 2,
            sortable: true,
        },        
        { 
            valueGetter: x => showSessionOption(x.data!), 
            headerName: "Rate",
            flex: 2,
            sortable: true
        },
        {
            field: "source",
            headerName: "Source",
            flex: 1,
            sortable: true,
            valueFormatter: x => x.value ?? SPOTSYNC_SOURCE,
        },
        {
            headerName: "Start",
            valueGetter: x => getSessionStartTime(x.data!),
            valueFormatter: x => formatDateTimeWithToday(x.value),
            filter: 'agDateColumnFilter',
            filterParams: {
                comparator: (filterLocalDateAtMidnight: Date, cellValue: LocalDateTime) => {
                    const cellDate = cellValue.toLocalDate();
                    const filterDate = LocalDateTime.of(
                        filterLocalDateAtMidnight.getFullYear(),
                        filterLocalDateAtMidnight.getMonth() + 1,
                        filterLocalDateAtMidnight.getDate(),
                        0, 0, 0
                    ).toLocalDate();
                    return cellDate.compareTo(filterDate);
                },
                buttons: ['reset', 'apply'],
                closeOnApply: true
            },
            floatingFilter: true,
            flex: 2,
            sortable: true,
            comparator: (valueA: LocalDateTime, valueB: LocalDateTime) => valueA.compareTo(valueB)
        },
        {
            headerName: "End",
            valueGetter: x => getSessionEndTime(x.data!),
            valueFormatter: x => formatDateTimeWithToday(x.value),
            filter: 'agDateColumnFilter',
            filterParams: {
                comparator: (filterLocalDateAtMidnight: Date, cellValue: LocalDateTime) => {
                    const cellDate = cellValue.toLocalDate();
                    const filterDate = LocalDateTime.of(
                        filterLocalDateAtMidnight.getFullYear(),
                        filterLocalDateAtMidnight.getMonth() + 1,
                        filterLocalDateAtMidnight.getDate(),
                        0, 0, 0
                    ).toLocalDate();
                    return cellDate.compareTo(filterDate);
                },
                buttons: ['reset', 'apply'],
                closeOnApply: true
            },
            floatingFilter: true,
            flex: 2,
            sortable: true,
            comparator: (valueA: LocalDateTime, valueB: LocalDateTime) => valueA.compareTo(valueB)
        },
        { 
            valueGetter: x => x.data!.vehicles,            
            headerName: "Plates",
            cellRenderer: PlateRenderer,
            flex: 3,
            sortable: true,
            comparator: (valueA: string[], valueB: string[]) => {
                const a = valueA?.join(', ') ?? '';
                const b = valueB?.join(', ') ?? '';
                return a.localeCompare(b);
            }
        },
        { 
            valueGetter: x => x.data!.reservation?.spaces,            
            headerName: "Spaces",
            flex: 1,
            sortable: true
        }
    ], []);

    const requestColumns = useMemo<ColDef<ServerRequestsParkingValidationData>[]>(() => [
    {
        field: "parkerName",
        headerName: "Parker Name",
        flex: 2,
        sortable: true,
    },
    {
        field: "parkerEmail",
        headerName: "Email",
        flex: 2,
        sortable: true,
    },
    {
        headerName: "Start Date & Time",
        valueGetter: x => x.data!.startDateTime,
        valueFormatter: x => {
        if (!x.value) return "";
        const instant = Instant.parse(x.value);
        const zoned = instant.atZone(ZoneId.systemDefault());
        const ldt = zoned.toLocalDateTime();

        return DateTimeFmt.min.format(ldt);
        },
        filter: 'agDateColumnFilter',
        filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue: string | LocalDateTime | null) => {
            if (!cellValue) return -1; 
            let ldt: LocalDateTime;
            if (typeof cellValue === 'string') {
            const instant = Instant.parse(cellValue);
            const zoned = instant.atZone(ZoneId.systemDefault());
            ldt = zoned.toLocalDateTime();
            } else {
            ldt = cellValue;
            }

            const cellDate = ldt.toLocalDate();

            const filterDate = LocalDate.of(
            filterLocalDateAtMidnight.getFullYear(),
            filterLocalDateAtMidnight.getMonth() + 1,
            filterLocalDateAtMidnight.getDate()
            );

            return cellDate.compareTo(filterDate);
        },
        buttons: ['reset', 'apply'],
        closeOnApply: true
        },
        floatingFilter: true,
        flex: 2,
        sortable: true,
        comparator: (valueA: LocalDateTime, valueB: LocalDateTime) => valueA.compareTo(valueB)
    },
    {
        headerName: "End Date & Time",
        valueGetter: x => x.data!.endDateTime,
        valueFormatter: x => {
        if (!x.value) return "";
        const instant = Instant.parse(x.value);
        const zoned = instant.atZone(ZoneId.systemDefault());
        const ldt = zoned.toLocalDateTime();

        return DateTimeFmt.min.format(ldt);
        },
        filter: 'agDateColumnFilter',
        filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue: string | LocalDateTime | null) => {
            if (!cellValue) return -1; 
            let ldt: LocalDateTime;
            if (typeof cellValue === 'string') {
            const instant = Instant.parse(cellValue);
            const zoned = instant.atZone(ZoneId.systemDefault());
            ldt = zoned.toLocalDateTime();
            } else {
            ldt = cellValue;
            }

            const cellDate = ldt.toLocalDate();

            const filterDate = LocalDate.of(
            filterLocalDateAtMidnight.getFullYear(),
            filterLocalDateAtMidnight.getMonth() + 1,
            filterLocalDateAtMidnight.getDate()
            );

            return cellDate.compareTo(filterDate);
        },
        buttons: ['reset', 'apply'],
        closeOnApply: true
        },
        flex: 2,
        sortable: true,
        floatingFilter: true,
        comparator: (valueA: LocalDateTime, valueB: LocalDateTime) => valueA.compareTo(valueB)
    },
    {
        field: "numberOfSpaces",
        headerName: "Spaces",
        flex: 1,
        sortable: true,
    },
    {
        headerName: "Requested Date",
        valueGetter: x => x.data!.requestedDate,
        valueFormatter: x => {
        if (!x.value) return "";
        const instant = Instant.parse(x.value);
        const zoned = instant.atZone(ZoneId.systemDefault());
        const ldt = zoned.toLocalDateTime();

        return DateTimeFmt.min.format(ldt);
        },
        filter: 'agDateColumnFilter',
        filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue: string | LocalDateTime | null) => {
            if (!cellValue) return -1; 
            let ldt: LocalDateTime;
            if (typeof cellValue === 'string') {
            const instant = Instant.parse(cellValue);
            const zoned = instant.atZone(ZoneId.systemDefault());
            ldt = zoned.toLocalDateTime();
            } else {
            ldt = cellValue;
            }

            const cellDate = ldt.toLocalDate();

            const filterDate = LocalDate.of(
            filterLocalDateAtMidnight.getFullYear(),
            filterLocalDateAtMidnight.getMonth() + 1,
            filterLocalDateAtMidnight.getDate()
            );

            return cellDate.compareTo(filterDate);
        },
        buttons: ['reset', 'apply'],
        closeOnApply: true
        },
        flex: 2,
        sortable: true,
        floatingFilter: true,
        comparator: (valueA: LocalDateTime, valueB: LocalDateTime) => valueA.compareTo(valueB)
    },
    {
      headerName: "",
      flex: 1,
      cellRenderer: (params: any) => {
        return (
          <>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="approve-request-tooltip">
                  Approve Request
                </Tooltip>
              }
            >
              <button
                className="btn btn-sm"  
                onClick={() => openConfirmModal("approve", params.data.validationRequestId)}    
              >
                <CheckCircle  className="me-1 icon-shift-up" size={16} />
              </button>
            </OverlayTrigger>
            <button
              className="btn btn-sm"
              onClick={() => openConfirmModal("reject", params.data.validationRequestId)}
              title="Reject"
            >
              <Trash  className="me-1 icon-shift-up" size={16} />
            </button>
          </>
        );
      },
      sortable: false,
      filter: false,
    },
    ], []);

    function openConfirmModal(action: "approve" | "reject", requestId: number) {
    setPendingAction({ action, requestId });
    setShowConfirm(true);
    }

    async function onConfirmAction() {
    if (!pendingAction) return;

    const { action, requestId } = pendingAction;

    if (action === "approve") {
        await approveValidationRequest(requestId);
    } else {
        await rejectValidationRequest(requestId);
    }

    setShowConfirm(false);
    setPendingAction(null);
    }

    async function approveValidationRequest(requestId: number) {
    try {
        const response = await validationApi.approveParkingValidationRequest({ requestId });
        if (response.data.error) {
            toast.error(response.data.error);
            return;
        } 
        setValidationRequestsState(prev => prev.filter(r => r.validationRequestId !== requestId));
        toast.success("Request Approved Successfully.");
    } catch {
        toast.error("Error approving request.");
    }
    }

    async function rejectValidationRequest(requestId: number) {
    try {
        const response = await validationApi.rejectParkingValidationRequest({ requestId });
        if (response.data.error) {
            toast.error(response.data.error);
            return;
        }   
        setValidationRequestsState(prev => prev.filter(r => r.validationRequestId !== requestId));
        toast.success("Request Rejected Successfully.");
    } catch {
        toast.error("Error rejecting request.");
    }
    }

    const filterBySearch = (list: Session[] | ServerRequestsParkingValidationData[]) => {
        if (list.length === 0) return list;
        
        if ('parker' in list[0]) {
            return (list as Session[]).filter((s) => 
                s.parker?.name?.toLowerCase().includes(search.toLowerCase()) ||
                s.vehicles?.toLowerCase().includes(search.toLowerCase()) ||
                s.reservation?.confirmationNum?.toLowerCase().includes(search.toLowerCase())
            );
        } else {
            return (list as ServerRequestsParkingValidationData[]).filter((r) => 
                r.parkerName?.toLowerCase().includes(search.toLowerCase()) ||
                r.parkerEmail?.toLowerCase().includes(search.toLowerCase())
            );
        }
    };

    const liveFiltered = search && liveSessions.length > 0 ? filterBySearch(liveSessions) as Session[] : liveSessions;
    const liveShown = liveFiltered.slice(0, liveVisibleCount);

    const upcomingFiltered = search && upcomingSessions.length > 0 ? filterBySearch(upcomingSessions) as Session[] : upcomingSessions;
    const upcomingShown = upcomingFiltered.slice(0, upcomingVisibleCount);

    const requestsFiltered = search && validationRequestsState.length > 0 ? filterBySearch(validationRequestsState) as ServerRequestsParkingValidationData[] : validationRequestsState;
    const requestsShown = requestsFiltered.slice(0, requestsVisibleCount);

    const previousFiltered = search && previousSessions.length > 0 ? filterBySearch(previousSessions) as Session[] : previousSessions;
    const previousShown = previousFiltered.slice(0, previousVisibleCount);

    const handleRefresh = () => {
        revalidator.revalidate();
    };

    const handleAutoRefreshToggle = useCallback( ( on: boolean ) => {
        if( on ) {
            autoRefreshStartedAtRef.current = Date.now();
            void runSessionPoll();
        }
        setAutoRefresh( on );
    }, [runSessionPoll] );

    const autoRefreshBar = (
        <div
            className="d-flex align-items-center justify-content-between px-3 py-2 rounded-3 mx-2 mt-2 mb-2"
            style={{ background: "rgba(214,31,132,0.07)" }}
        >
            <Form.Check
                type="switch"
                id="session-auto-refresh"
                label={
                    <span className="small">
                        Auto-refresh <span className="text-muted">· 30s</span>
                    </span>
                }
                checked={autoRefresh}
                onChange={ ( e ) => handleAutoRefreshToggle( e.target.checked ) }
                className="user-select-none"
            />
            <div className="d-flex align-items-center gap-2 text-muted small">
                <span>{ lastRefreshedAt ? formatLastRefreshClock( lastRefreshedAt ) : "—" }</span>
                <span>·</span>
                <ArrowClockwise className={ `${ isSessionPollLoading ? "spin" : "" }` } />
            </div>
        </div>
    );

    const onGridReady = (params: GridReadyEvent) => { params.api.sizeColumnsToFit(); };
    const { claims } = useAuthorization();
    
    const handleSessionClick = useCallback( ( data: Session ) => {
        if ( !userCanNavigateToParkerFromSessions( claims.roles ) ) {
            toast.error( "You are not authorized to view parker details." );
            return;
        }
        if ( data.type === SessionType.Subscription ) {
            const sub = data.subscription!;
            nav( `/facility/${facilityId}/parker/${sub.parkerId}/plan/${sub.subscriptionId}`, { state: { from: 'status' } } );
        }
        if ( data.type === SessionType.SubscriptionParker ) {
            const guest = data.subscriptionGuest!;
            nav( `/facility/${facilityId}/parker/${guest.parkerId}/plan/${guest.subscriptionId}`, { state: { from: 'status' } } );
        }
        if ( data.type === SessionType.Reservation ) {
            const res = data.reservation!;
            nav( `/facility/${facilityId}/parker/${res.parkerId}/overview`, { state: { from: 'status' } } );
        }
    }, [claims.roles, facilityId, nav] );

    const onRowClicked = ( event: RowClickedEvent<any> ) => handleSessionClick( event.data );
    
    return <Container fluid className="h-100" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Row className="h-100" style={{ marginLeft: "10px", marginRight: 0 }}>
            <Col
                className="h-100"
                style={{
                    display: "grid",
                    gridTemplateRows: "auto auto 1fr 5px",
                    gridAutoColumns: "minmax(0, 1fr)",
                    overflow: "hidden",
                    paddingLeft: 0,
                    paddingRight: 0,
                }}
            >
                <div
                    className="d-flex flex-row flex-nowrap align-items-start justify-content-between gap-2 pe-2 pb-1 flex-shrink-0 w-100"
                >
                    <div className="min-w-0 flex-grow-1 me-1">
                        <PageTitle>Transient Sessions</PageTitle>
                    </div>
                    {qrEnabled && facilityCache.getFacility( facilityId )?.timeZone && (
                        <QrEntriesTodayStat count={entryConfirmedTodayCount} />
                    )}
                </div>
                <Tabs
                    defaultActiveKey={tab}
                    activeKey={tab}
                    onSelect={(k) => nav(`/facility/${facilityId}/status/${k}`)}
                    className="w-100 border-bottom-0"
                    variant="underline"
                    justify>
                    <Tab eventKey={SessionTab.Live} 
                         title={<div className="d-flex-align-center justify-content-center">
                             Active {liveSessions.length !== 0 && <Badge className="rounded-5 d-flex fw-normal justify-content-center px-2">{liveSessions.length}</Badge>}
                         </div>}
                         className="h-100">
                        <div className="d-none d-md-flex flex-column" style={{ height: "100%", width: "100%" }}>
                            {autoRefreshBar}
                            <div style={{ flex: "1 1 0", minHeight: 0 }}>
                                <DataGrid
                                    rowData={liveSessions}
                                    columnDefs={activeColumns}
                                    onGridReady={onGridReady}
                                    onRowClicked={onRowClicked}
                                />
                            </div>
                        </div>

                        <div className="d-block d-md-none">
                            <div className="mb-2 mt-3 px-1 sticky-top bg-white">
                                <Form.Control
                                    type="search"
                                    placeholder="Search sessions..."
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setLiveVisibleCount(10);
                                    }}
                                    className="mb-2"
                                />
                                {autoRefreshBar}
                            </div>

                            {liveShown.length === 0 ? (
                                <div className="text-center text-muted py-3">
                                    No Rows to Show
                                </div>
                            ) : (
                                liveShown.map( ( session, idx ) => (
                                    <SessionMobileCard
                                        key={idx}
                                        session={session}
                                        onClick={() => handleSessionClick( session )}
                                    />
                                ) )
                            )}

                            {liveVisibleCount < liveFiltered.length && liveShown.length > 0 && (
                                <div className="text-center">
                                    <Button
                                        className="btn-main"
                                        onClick={() => setLiveVisibleCount((v) => v + 10)}
                                    >
                                        Load More
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Tab>
                    <Tab eventKey={SessionTab.Upcoming}
                         title={<div className="d-flex-align-center justify-content-center">
                             Upcoming
                         </div>}
                         className="h-100">
                        <div className="d-none d-md-block" style={{ height: "100%", width: '100%' }}>
                            <DataGrid
                                rowData={upcomingSessions}
                                columnDefs={activeColumns}
                                onGridReady={onGridReady}
                                onRowClicked={onRowClicked}
                            />
                        </div>

                        <div className="d-block d-md-none">
                            <Form.Control
                                type="search"
                                placeholder="Search sessions..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setUpcomingVisibleCount(10);
                                }}
                                className="mb-3 mt-3 sticky-top bg-white"
                            />

                            {upcomingShown.length === 0 ? (
                                <div className="text-center text-muted py-3">
                                    No Rows to Show
                                </div>
                            ) : (
                                upcomingShown.map( ( session, idx ) => (
                                    <SessionMobileCard
                                        key={idx}
                                        session={session}
                                        onClick={() => handleSessionClick( session )}
                                    />
                                ) )
                            )}

                            {upcomingVisibleCount < upcomingFiltered.length && upcomingShown.length > 0 && (
                                <div className="text-center">
                                    <Button
                                        className="btn-main"
                                        onClick={() => setUpcomingVisibleCount((v) => v + 10)}
                                    >
                                        Load More
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Tab>
                    <Tab eventKey={SessionTab.Requests}
                         title={<div className="d-flex-align-center justify-content-center">
                             Requests {validationRequestsState.length !== 0 && <Badge className="rounded-5 d-flex fw-normal justify-content-center px-2">{validationRequestsState.length}</Badge>}
                         </div>}
                         className="h-100">
                        <div className="d-none d-md-block" style={{ height: "100%", width: '100%' }}>
                            <DataGrid
                                rowData={validationRequestsState}
                                columnDefs={requestColumns}
                                onGridReady={onGridReady}
                            />
                        </div>

                        <div className="d-block d-md-none">
                            <Form.Control
                                type="search"
                                placeholder="Search requests..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setRequestsVisibleCount(10);
                                }}
                                className="mb-3 mt-3 sticky-top bg-white"
                            />

                            {requestsShown.length === 0 ? (
                                <div className="text-center text-muted py-3">
                                    No Rows to Show
                                </div>
                            ) : (
                                requestsShown.map((request, idx) => (
                                    <Card
                                        key={idx}
                                        className="m-3 rounded-4 border-1 mx-1"
                                    >
                                        <Card.Body className="p-3">
                                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                                                <h6 className="mb-0 fw-bold">{request.parkerName}</h6>
                                            </div>

                                            <div className="mb-2">
                                                <div className="text-muted small">EMAIL</div>
                                                <div className="fw-bold">{request.parkerEmail}</div>
                                            </div>

                                            <div className="d-flex gap-2 mb-3">
                                                <Button
                                                    size="sm"
                                                    className="btn-main flex-grow-1"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (request.validationRequestId !== undefined) {
                                                            openConfirmModal("approve", request.validationRequestId);
                                                        }
                                                    }}
                                                >
                                                    <CheckCircle className="me-1 icon-shift-up" size={16} />
                                                    Approve
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="light"
                                                    className="flex-grow-1"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (request.validationRequestId !== undefined) {
                                                            openConfirmModal("reject", request.validationRequestId);
                                                        }
                                                    }}
                                                >
                                                    <Trash className="me-1 icon-shift-up" size={16} />
                                                    Reject
                                                </Button>
                                            </div>

                                            <div className="row g-2 mb-3">
                                                <div className="col-6">
                                                    <div className="bg-light rounded-3 py-2 px-2">
                                                        <div className="text-muted small">START</div>
                                                        <div className="fw-bold fs-6">
                                                            {request.startDateTime ? (() => {
                                                                const instant = Instant.parse(String(request.startDateTime));
                                                                const zoned = instant.atZone(ZoneId.systemDefault());
                                                                const ldt = zoned.toLocalDateTime();
                                                                return DateTimeFmt.min.format(ldt);
                                                            })() : '—'}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-6">
                                                    <div className="bg-light rounded-3 py-2 px-2">
                                                        <div className="text-muted small">END</div>
                                                        <div className="fw-bold fs-6">
                                                            {request.endDateTime ? (() => {
                                                                const instant = Instant.parse(String(request.endDateTime));
                                                                const zoned = instant.atZone(ZoneId.systemDefault());
                                                                const ldt = zoned.toLocalDateTime();
                                                                return DateTimeFmt.min.format(ldt);
                                                            })() : '—'}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-6">
                                                    <div className="bg-light rounded-3 py-2 px-2">
                                                        <div className="text-muted small">SPACES</div>
                                                        <div className="fw-bold fs-6">{request.numberOfSpaces}</div>
                                                    </div>
                                                </div>

                                                <div className="col-6">
                                                    <div className="bg-light rounded-3 py-2 px-2">
                                                        <div className="text-muted small">REQUESTED</div>
                                                        <div className="fw-bold fs-6">
                                                            {request.requestedDate ? (() => {
                                                                const instant = Instant.parse(String(request.requestedDate));
                                                                const zoned = instant.atZone(ZoneId.systemDefault());
                                                                const ldt = zoned.toLocalDateTime();
                                                                return DateTimeFmt.min.format(ldt);
                                                            })() : '—'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                ))
                            )}

                            {requestsVisibleCount < requestsFiltered.length && requestsShown.length > 0 && (
                                <div className="text-center">
                                    <Button
                                        className="btn-main"
                                        onClick={() => setRequestsVisibleCount((v) => v + 10)}
                                    >
                                        Load More
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Tab>
                    <Tab eventKey={SessionTab.Previous}
                         title={<div className="d-flex-align-center justify-content-center">
                             <span className="d-none d-sm-inline">7 Day History</span>
                             <span className="d-inline d-sm-none">History</span>
                         </div>}
                         className="h-100">
                        <div className="d-none d-md-block" style={{ height: "100%", width: '100%' }}>
                            <DataGrid
                                rowData={previousSessions}
                                columnDefs={activeColumns}
                                onGridReady={onGridReady}
                                onRowClicked={onRowClicked}
                            />
                        </div>

                        <div className="d-block d-md-none">
                            <Form.Control
                                type="search"
                                placeholder="Search sessions..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPreviousVisibleCount(10);
                                }}
                                className="mb-3 mt-3 sticky-top bg-white"
                            />

                            {previousShown.length === 0 ? (
                                <div className="text-center text-muted py-3">
                                    No Rows to Show
                                </div>
                            ) : (
                                previousShown.map( ( session, idx ) => (
                                    <SessionMobileCard
                                        key={idx}
                                        session={session}
                                        onClick={() => handleSessionClick( session )}
                                    />
                                ) )
                            )}

                            {previousVisibleCount < previousFiltered.length && previousShown.length > 0 && (
                                <div className="text-center">
                                    <Button
                                        className="btn-main"
                                        onClick={() => setPreviousVisibleCount((v) => v + 10)}
                                    >
                                        Load More
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Tab>
                </Tabs>
            </Col>
        </Row>
        <ConfirmModal
        title={`${pendingAction?.action === "approve" ? "Approve" : "Reject"} Validation Request`}
        message={`Are you sure you want to ${pendingAction?.action} this validation request?`}
        show={showConfirm}
        setShow={setShowConfirm}
        onConfirm={onConfirmAction}
        />
        {qrEnabled && (
            <>
                <OverlayTrigger placement="left" overlay={<Tooltip id="scan-qr-tooltip">Scan QR</Tooltip>}>
                    <button
                        type="button"
                        className="btn btn-main rounded-circle shadow p-3 position-fixed d-flex align-items-center justify-content-center"
                        style={{ right: "1.5rem", bottom: alprEnabled ? "6rem" : "1.5rem", zIndex: 1050 }}
                        onClick={() => setShowQrScanModal(true)}
                        aria-label="Scan QR"
                    >
                        <QrCode size={24} />
                    </button>
                </OverlayTrigger>
                <QrScannerModal
                    show={showQrScanModal}
                    onHide={() => setShowQrScanModal(false)}
                    onQrResultShown={ ( info ) => {
                        // Active or upcoming: pass reservation to entry-confirm. Do not use API response for any UI.
                        if ( ( info.status !== "active" && info.status !== "upcoming" ) || info.reservationId == null ) return;
                        void reservationApi
                            .entryConfirmReservation( {
                                facilityId,
                                serverRequestsReservationEntryConfirmBody: { reservationId: info.reservationId },
                            } )
                            .then( ( res ) => {
                                if ( res.data?.wasSuccessful ) void refreshEntryConfirmedTodayCount();
                            } )
                            .catch( ( e: unknown ) => {
                                if ( import.meta.env.DEV ) console.error( "reservation/entry-confirm", e );
                            } );
                    } }
                    getConfirmationSession={(confirmationNum): QrSessionInfo => {
                        const q = confirmationNum.trim().toLowerCase();
                        if (!q) return { status: "not_found" };
                        const dateTimeFmt = DateTimeFormatter.ofPattern("MMM d, h:mm a").withLocale(Locale.US);
                        const timeFmt = DateTimeFormatter.ofPattern("h:mm a").withLocale(Locale.US);
                        const fmt = (dt: LocalDateTime) =>
                            dt.toLocalDate().equals(LocalDate.now()) ? timeFmt.format(dt) : dateTimeFmt.format(dt);
                        const toInfo = (s: Session, status: QrSessionInfo["status"]): QrSessionInfo => ({
                            status,
                            from:  fmt(getSessionStartTime(s)),
                            to:    fmt(getSessionEndTime(s)),
                            plate: s.vehicles?.trim() || undefined,
                            reservationId: s.reservation?.reservationId,
                        });
                        const live = liveSessions.find((s) => s.reservation?.confirmationNum?.toLowerCase() === q);
                        if (live) return toInfo(live, "active");
                        const upcoming = upcomingSessions.find((s) => s.reservation?.confirmationNum?.toLowerCase() === q);
                        if (upcoming) return toInfo(upcoming, "upcoming");
                        const previous = previousSessions.find((s) => s.reservation?.confirmationNum?.toLowerCase() === q);
                        if (previous) return toInfo(previous, "expired");
                        return { status: "not_found" };
                    }}
                />
            </>
        )}
        {alprEnabled && (
            <>
                <OverlayTrigger placement="left" overlay={<Tooltip id="scan-plate-tooltip">Scan plate</Tooltip>}>
                    <button
                        type="button"
                        className="btn btn-main rounded-circle shadow p-3 position-fixed d-flex align-items-center justify-content-center"
                        style={{ right: "1.5rem", bottom: "1.5rem", zIndex: 1050 }}
                        onClick={() => setShowPlateScanModal(true)}
                        aria-label="Scan plate"
                    >
                        <Camera size={24} />
                    </button>
                </OverlayTrigger>
                <PlateScannerModal
                    show={showPlateScanModal}
                    onHide={() => setShowPlateScanModal(false)}
                    getPlateSession={(plate): PlateSessionInfo | null => {
                        const q = plate.trim().toLowerCase();
                        if (!q) return null;
                        const dateTimeFmt = DateTimeFormatter.ofPattern("MMM d, h:mm a").withLocale(Locale.US);
                        const timeFmt = DateTimeFormatter.ofPattern("h:mm a").withLocale(Locale.US);
                        const fmt = (dt: LocalDateTime) =>
                            dt.toLocalDate().equals(LocalDate.now()) ? timeFmt.format(dt) : dateTimeFmt.format(dt);
                        const live = liveSessions.find((s) => s.vehicles?.toLowerCase().includes(q));
                        if (live) return {
                            status: "active",
                            from: fmt(getSessionStartTime(live)),
                            to:   fmt(getSessionEndTime(live)),
                        };
                        const upcoming = upcomingSessions.find((s) => s.vehicles?.toLowerCase().includes(q));
                        if (upcoming) return {
                            status: "upcoming",
                            from: fmt(getSessionStartTime(upcoming)),
                            to:   fmt(getSessionEndTime(upcoming)),
                        };
                        const previous = previousSessions.find((s) => s.vehicles?.toLowerCase().includes(q));
                        if (previous) return {
                            status: "expired",
                            from: fmt(getSessionStartTime(previous)),
                            to:   fmt(getSessionEndTime(previous)),
                        };
                        return { status: "not_found" };
                    }}
                />
            </>
        )}
    </Container>;
}